import { useState } from "react";
import { useParams } from "react-router";
import ms from "ms";
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { CustomerPaymentStatus, ICustomerPaymentListParams } from "shared";

import { useFetchCustomerPaymentsQuery } from "../services/api/customerPaymentsApi/customerPayments";

export function buildCustomerPaymentParams(
  filterModel: GridFilterModel,
  sortModel: GridSortModel,
  pagination: GridPaginationModel,
): ICustomerPaymentListParams {
  return {
    page: pagination.page + 1,
    limit: pagination.pageSize,
    status: (filterModel.items.find(item => item.field === `status`)?.value as CustomerPaymentStatus) || null,
    contactName: filterModel.items.find(item => item.field === `contact`)?.value || null,
    searchText: filterModel.quickFilterValues?.[0] || null,
    sortColumn: sortModel[0]?.field || null,
    sortType: sortModel[0]?.sort || null,
  };
}

/** Server-side paginated, filtered & sorted customer (Stripe) payments table data */
export function useCustomerPayments() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });

  const result = useFetchCustomerPaymentsQuery({
    ...buildCustomerPaymentParams(filterModel, sortModel, paginationModel),
    organisationId,
  }, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
    pollingInterval: ms(`5 min`),
    skipPollingIfUnfocused: true,
  });

  return {
    ...result,
    count: result.data?.count ?? 0,
    filterModel,
    sortModel,
    paginationModel,
    // A narrower filter can leave the current page past the last result, so go back to page 0
    setFilterModel: (model: GridFilterModel) => {
      setFilterModel(model);
      setPaginationModel(p => ({ ...p, page: 0 }));
    },
    setSortModel,
    setPaginationModel,
  };
}
